import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  FormGroup,
  Stack,
  Typography
} from '@mui/material'
import React from 'react'
import { FixedSizeList } from 'react-window'

import Icon from 'components/Icon'
import { KubernetesTagLabelSectionListItem } from 'components/services/create/target-selection/kubernetes/KubernetesTagLabelSectionListItem'
import { useHeightMeasurement } from 'hooks/use-height-measurement'
import { K8sAutocompleteSearchOption } from 'types/forms'

export const KubernetesTagLabelSection: React.FC<{
  label: string
  options: K8sAutocompleteSearchOption[]
  selectedOptions: K8sAutocompleteSearchOption[]
  handleSelectOption(e, option): void
}> = ({ label, options, selectedOptions, handleSelectOption }) => {
  const selectedOptionsUIDs = selectedOptions.map(
    (selectedOption) => selectedOption.uid
  )

  // how many items we want to display at a time
  const virtualizedListLength = 8
  // this controls how many additional list items above and below our current window are pre-rendered by react-window
  // this is an optimization so that when the user starts scrolling they won't see blank entries in the list
  const overscanCount = virtualizedListLength

  // this is a manual measurement of the list item height as rendered in the browser
  const assumedHeight = 58
  // instead of trusting that to always be accurate we'll use that as the default and then measure
  // the first item in the list to be sure that we're using an accurate value
  const { height: listItemHeight, measurementRef: listItemRef } =
    useHeightMeasurement<HTMLDivElement>(assumedHeight)
  // make the scrollable list area a multiple of the list item height
  // semi-arbitrary value of 8 since that ends up with a reasonably-sized scroll area
  const virtualizedListHeight = listItemHeight * virtualizedListLength

  // determine if we need to bother with virtualization or not
  const isVirtualizationNeeded = options.length > virtualizedListLength

  return (
    <Accordion
      disableGutters
      data-test-target-param-section={label}
      sx={{ padding: 1 }}
    >
      <AccordionSummary
        data-test-target-param-section-summary
        expandIcon={<Icon icon="solidCaretDown" />}
      >
        <Stack alignItems="center" direction="row" display="flex" gap={1}>
          <Typography color="text.secondary">{label}</Typography>
          <Chip label={options.length} size="small" />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <FormGroup
          data-test-virtualized-list
          css={{
            height: isVirtualizationNeeded ? virtualizedListHeight : 'auto'
          }}
        >
          {isVirtualizationNeeded ? (
            <FixedSizeList
              height={virtualizedListHeight}
              itemCount={options.length}
              itemSize={listItemHeight}
              overscanCount={overscanCount}
            >
              {({ index, style }) => {
                const option = options[index]
                return (
                  <KubernetesTagLabelSectionListItem
                    key={option.uid}
                    ref={index === 0 ? listItemRef : null} // we only want to check the first item
                    css={style}
                    handleSelectOption={handleSelectOption}
                    isLastOption={index === options.length - 1}
                    isSelected={selectedOptionsUIDs.includes(option.uid)}
                    option={option}
                  />
                )
              }}
            </FixedSizeList>
          ) : (
            options.map((option, index) => (
              <KubernetesTagLabelSectionListItem
                key={option.uid}
                ref={index === 0 ? listItemRef : null} // we only want to check the first item
                handleSelectOption={handleSelectOption}
                isLastOption={index === options.length - 1}
                isSelected={selectedOptionsUIDs.includes(option.uid)}
                option={option}
              />
            ))
          )}
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  )
}
